import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AuthError, AuthApiError } from "@supabase/supabase-js";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CountrySelect } from "./CountrySelect";

export const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [companyCountry, setCompanyCountry] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { toast } = useToast();

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setCompanyName("");
    setCompanyAddress("");
    setCompanyCountry("");
    setErrorMessage("");
  };

  const getErrorMessage = (error: AuthError) => {
    if (error instanceof AuthApiError) {
      switch (error.status) {
        case 400:
          if (error.message.includes("Invalid login credentials")) {
            return "Invalid email or password. Please check your credentials and try again.";
          }
          break;
        case 422:
          return "Invalid email format. Please enter a valid email address.";
        default:
          return error.message;
      }
    }
    return "An unexpected error occurred. Please try again.";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    setErrorMessage("");

    if (isSignUp) {
      const trimmedCompanyName = companyName.trim();
      const trimmedCompanyAddress = companyAddress.trim();
      const trimmedCompanyCountry = companyCountry.trim();

      if (!trimmedCompanyName || !trimmedCompanyAddress || !trimmedCompanyCountry) {
        setErrorMessage("Company name, address, and country are required");
        return;
      }

      setIsLoading(true);
      try {
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              company_name: trimmedCompanyName,
              company_address: trimmedCompanyAddress,
              company_country: trimmedCompanyCountry,
            },
          },
        });

        if (signUpError) throw signUpError;

        toast({
          title: "Account created",
          description: "Please check your email to verify your account",
        });

        setIsLoading(false);
        setIsSignUp(false);
        resetForm();
      } catch (error: any) {
        console.error("Signup error:", error);
        setErrorMessage(getErrorMessage(error));
        setIsLoading(false);
      }
    } else {
      setIsLoading(true);
      try {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;
      } catch (error: any) {
        console.error("Login error:", error);
        setErrorMessage(getErrorMessage(error));
        setIsLoading(false);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {errorMessage && (
        <Alert variant="destructive">
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isLoading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          placeholder="Enter your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={isLoading}
        />
      </div>

      {isSignUp && (
        <>
          <div className="space-y-2">
            <Label htmlFor="companyName">Company Name</Label>
            <Input
              id="companyName"
              placeholder="Enter your company name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="companyAddress">Company Address</Label>
            <Input
              id="companyAddress"
              placeholder="Enter your company address"
              value={companyAddress}
              onChange={(e) => setCompanyAddress(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="companyCountry">Country</Label>
            <CountrySelect
              value={companyCountry}
              onValueChange={setCompanyCountry}
              disabled={isLoading}
            />
          </div>
        </>
      )}

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? "Please wait..." : (isSignUp ? "Sign Up" : "Sign In")}
      </Button>

      <div className="text-center">
        <Button
          variant="link"
          onClick={() => {
            setIsSignUp(!isSignUp);
            resetForm();
          }}
          className="text-sm"
          disabled={isLoading}
        >
          {isSignUp
            ? "Already have an account? Sign in"
            : "Don't have an account? Sign up"}
        </Button>
      </div>
    </form>
  );
};
